import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { supabase } from '@/integrations/supabase/client';
import TwoFactorVerify from '../components/TwoFactorVerify';
import logoSrc from '@/assets/snap-chart-logo-final.png';

const { FiAlertCircle, FiShield, FiLock, FiArrowLeft, FiZap, FiTarget, FiDollarSign } = FiIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockMinutes, setLockMinutes] = useState(0);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState('totp');
  const [phoneLast4, setPhoneLast4] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Where to send the user after a successful login. Defaults to the
  // dashboard, but callers can pass ?returnTo=/some/path (e.g. the
  // Founding Member page needs the user back on /founding to complete
  // the seat claim). Only accept in-app relative paths — never absolute
  // URLs — so this can't be used as an open-redirect.
  const rawReturnTo = searchParams.get('returnTo') || '';
  const postLoginDest = /^\/[^/]/.test(rawReturnTo) ? rawReturnTo : '/dashboard';

  useEffect(() => {
    if (user) {
      navigate(postLoginDest, { replace: true });
    }
  }, [user, navigate, postLoginDest]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: checkResult } = await supabase.rpc('check_login_allowed', {
        p_email: email.toLowerCase(),
      });

      if (checkResult && !checkResult.allowed) {
        setIsLocked(true);
        setLockMinutes(checkResult.remaining_minutes || 15);
        setError(checkResult.reason || 'Too many failed attempts. Please try again later.');
        setLoading(false);
        return;
      }

      const { data: twoFACheck } = await supabase.rpc('check_user_2fa', {
        p_email: email.toLowerCase(),
      });

      if (twoFACheck?.requires_2fa) {
        setPendingCredentials({ email, password });
        setTwoFAMethod(twoFACheck.method || 'totp');
        setPhoneLast4(twoFACheck.phone_last_4 || '');
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      await completeLogin(email, password, checkResult);
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const completeLogin = async (loginEmail, loginPassword, checkResult = null) => {
    setLoading(true);
    try {
      const { data, error: loginError } = await login(loginEmail, loginPassword);

      if (loginError) {
        await supabase.rpc('log_login_attempt', {
          p_email: loginEmail.toLowerCase(),
          p_success: false,
        });

        if (loginError.message.includes('Invalid login credentials')) {
          const remaining = checkResult?.attempts_remaining || 5;
          setError(`Invalid email or password. ${remaining - 1} attempts remaining.`);
        } else {
          setError(loginError.message);
        }
        setLoading(false);
      } else if (data?.user) {
        await supabase.rpc('log_login_attempt', {
          p_email: loginEmail.toLowerCase(),
          p_success: true,
        });
        setTimeout(() => navigate(postLoginDest, { replace: true }), 100);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handle2FASuccess = () => {
    if (pendingCredentials) {
      completeLogin(pendingCredentials.email, pendingCredentials.password);
    }
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setTwoFAMethod('totp');
    setPhoneLast4('');
    setPendingCredentials(null);
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        // Preserve ?returnTo across the Google OAuth roundtrip so the
        // Founding Member / feedback flows land users back where they
        // started after Google auth. Falls back to /dashboard.
        options: { redirectTo: `${window.location.origin}${postLoginDest}` },
      });
      if (error) {
        setError(error.message);
        setLoading(false);
      }
    } catch (err) {
      console.error('Google Sign-In error:', err);
      setError('Failed to sign in with Google. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black text-white flex relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-40 w-[40rem] h-[40rem] bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-40 w-[40rem] h-[40rem] bg-cyan-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* ═══════════ LEFT — Brand panel (desktop only) ═══════════ */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[40%] relative z-10 flex-col justify-between p-12 border-r border-white/5">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-gray-500 hover:text-cyan-400 transition mb-12">
            <SafeIcon icon={FiArrowLeft} /> back to snap-chart.ai
          </Link>

          <div className="flex items-center gap-3 mb-10">
            <img src={logoSrc} alt="SnapChart AI" className="h-12 w-12" />
            <span className="font-bold text-xl bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              SnapChart AI
            </span>
          </div>

          <h2 className="text-4xl xl:text-5xl font-black tracking-tight leading-[1.05] mb-4">
            <span className="text-white">Welcome back,</span><br />
            <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">trader.</span>
          </h2>
          <p className="text-base text-gray-400 leading-relaxed max-w-md">
            Your AI Risk Officer is waiting. Pull up a chart, get a verdict in 2 seconds.
          </p>
        </div>

        {/* Quick stats teaser */}
        <div className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600 mb-4">
            ↓ live · last 90 days · across all users
          </div>
          {[
            { icon: FiTarget, label: 'NO-TRADE vetoes called right', value: '188', color: 'purple' },
            { icon: FiDollarSign, label: 'Capital preserved by AI discipline', value: '$99,736', color: 'emerald' },
            { icon: FiZap, label: 'Avg verdict time', value: '1.8s', color: 'cyan' },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.05 }}
              className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color === 'purple' ? 'bg-purple-500/15 text-purple-400' : s.color === 'emerald' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-cyan-500/15 text-cyan-400'}`}>
                <SafeIcon icon={s.icon} className="text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-mono font-black text-xl tabular-nums ${s.color === 'purple' ? 'text-purple-300' : s.color === 'emerald' ? 'text-emerald-300' : 'text-cyan-300'}`}>
                  {s.value}
                </div>
                <div className="text-[11px] text-gray-500">{s.label}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ═══════════ RIGHT — Form ═══════════ */}
      <div className="flex-1 relative z-10 flex flex-col">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-white/5">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-gray-400 hover:text-cyan-400 transition">
            <SafeIcon icon={FiArrowLeft} /> back
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src={logoSrc} alt="" className="h-8 w-8" />
            <span className="font-bold text-sm bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">SnapChart AI</span>
          </Link>
          <div className="w-12" />
        </header>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            {/* Form heading (only on mobile, hidden on desktop where the left panel takes the headline) */}
            <div className="lg:hidden mb-8 text-center">
              <h1 className="text-3xl font-black tracking-tight mb-2">
                <span className="text-white">Welcome back, </span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">trader.</span>
              </h1>
              <p className="text-sm text-gray-400">Sign in to your AI Risk Officer</p>
            </div>

            <div className="hidden lg:block mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400 mb-3">
                sign in
              </span>
              <h1 className="text-3xl font-black tracking-tight text-white">Log in to your account</h1>
            </div>

            {/* Locked Message */}
            {isLocked && (
              <div className="flex items-center p-4 space-x-3 text-sm text-orange-200 bg-orange-500/10 rounded-xl border border-orange-500/30 mb-5">
                <SafeIcon icon={FiLock} className="w-6 h-6 flex-shrink-0 text-orange-400" />
                <div>
                  <p className="font-semibold">Account temporarily locked</p>
                  <p className="text-orange-300/80 text-xs">Too many failed attempts. Try again in {lockMinutes} minutes.</p>
                </div>
              </div>
            )}

            {requires2FA ? (
              <TwoFactorVerify
                email={pendingCredentials?.email}
                method={twoFAMethod}
                phoneLast4={phoneLast4}
                onSuccess={handle2FASuccess}
                onCancel={handle2FACancel}
              />
            ) : (
              <>
                {error && !isLocked && (
                  <div className="flex items-center p-3 space-x-2 text-sm text-red-200 bg-red-500/10 rounded-lg border border-red-500/30 mb-5">
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-2">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="w-full py-3.5 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                      required
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label htmlFor="password" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400">
                        Password
                      </label>
                      <Link to="/forgot-password" className="text-xs font-mono uppercase tracking-[0.15em] text-cyan-400 hover:text-cyan-300">
                        forgot?
                      </Link>
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full py-3.5 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 text-base font-bold text-black bg-gradient-to-r from-cyan-400 to-purple-400 rounded-xl hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-xl shadow-cyan-500/20"
                  >
                    {loading ? 'Signing in...' : 'Log in →'}
                  </button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/5"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3 bg-gray-900/0 text-gray-600 font-mono uppercase tracking-[0.2em]">or</span>
                  </div>
                </div>

                <button
                  onClick={handleGoogleSignIn}
                  type="button"
                  className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white/[0.03] border border-white/10 rounded-xl hover:bg-white/[0.06] hover:border-white/20 transition-all duration-200 text-gray-200 font-medium"
                >
                  <SafeIcon icon={FcGoogle} className="w-5 h-5" />
                  Continue with Google
                </button>

                <p className="text-sm text-center text-gray-400 mt-8">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-bold text-cyan-400 hover:text-cyan-300">
                    Sign up free →
                  </Link>
                </p>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
